import {
  ApplicationRef,
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConflictComponent } from 'app/calendar/conflict/conflict';
import { NewEventComponent } from 'app/calendar/newEvent/newEvent';
import { StyleComponent } from 'app/components/style/style.component';
import { TooltipComponent } from 'app/components/tooltip/tooltip';
import { Config } from 'app/config.const';
import { Agenda } from 'app/model/agenda';
import { IIntervallo } from 'app/model/ambulatorio';
import { IDeleteRequest } from 'app/model/appuntamenti';
import { IAppuntamento } from 'app/model/appuntamento';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

import 'fullcalendar';
import { EventObjectInput, View } from 'fullcalendar';
import { OptionsInput } from 'fullcalendar/src/types/input-types';

import { isNumber } from '@ng-bootstrap/ng-bootstrap/util/util';
import { CalendarBase } from 'app/calendar/calendar_base';
import 'qtip2';
import { buffer, debounceTime, delay, filter, map, switchMap } from 'rxjs/operators';
import { WebSocketReadyState } from '../../angular-rest';

@Component({
  selector: 'full-calendar',
  templateUrl: 'fullcalendar.html',
  exportAs: 'calendar'
})
export class FullCalendarComponent extends CalendarBase implements OnDestroy {
  private static creationRequestId = -1;

  private _container: ElementRef;
  @ViewChild('calendar')
  set container(value: ElementRef) {
    this._container = value;
  }

  get container(): ElementRef {
    return this._container;
  }

  private _agenda: Agenda;
  @Input()
  set agenda(value: Agenda) {
    this._agenda = value;
    this.init();
  }

  get agenda(): Agenda {
    return this._agenda;
  }

  private readonly toolTipFactory: ComponentFactory<TooltipComponent>;
  private slotDuration: moment.Duration;

  private newEvent = new Subject<moment.Moment>();
  private updateQueue = new Subject<IAppuntamento>();
  private deleteQueue = new Subject<number>();

  private internalSubscription = new Subscription();
  private newEventSubscription: Subscription;

  constructor(
    private injector: Injector,
    private modalService: NgbModal,
    private app: ApplicationRef,
    private resolver: ComponentFactoryResolver,
  ) {
    super();

    // Questa mi serve per iniettare in modo dinamico il tooltip.
    this.toolTipFactory = this.resolver.resolveComponentFactory(TooltipComponent);

    // Questa subscription invece mi trasforma i doppi click in delle eventRequests.
    this.newEventSubscription = this.newEvent.pipe(
      buffer(this.newEvent.pipe(debounceTime(Config.doubleClickTime))),
      filter(m => m.length === 2)
    ).subscribe(m => {
      const date = m[0];

      this.upsertEvent({
        version: 1,
        categoria: "Paziente",
        evento: "Visita ambulatoriale",
        title: '',
        start: date.clone(),
        end: date.hasTime() ? date.clone().add(this.slotDuration) : null,
        status: 'U'
      });
    });


  }

  protected get settings(): Observable<OptionsInput> {
    return this.agenda.impostazioni.pipe(map(settings => {
      this.slotDuration = moment.duration(settings.intervallo);
      return <OptionsInput>{
        weekends: settings.mostra_weekend,
        minTime: moment.duration(settings.inizio_giornata),
        maxTime: moment.duration(settings.fine_giornata),
        defaultView: settings.vista
      };
    }))
  }

  // FullCalendar events.
  protected onEventClick(element: HTMLElement, calEvent: EventObjectInput) {
    $(element).qtip('hide');
    this.upsertEvent(calEvent as IAppuntamento);
  }

  protected onDayClick(_, date: moment.Moment) {
    this.newEvent.next(date);
  }

  protected onEventDestroy(event: EventObjectInput, element: JQuery, view: View) {
    element.trigger('destroy');
  }

  protected onEventRender(event: EventObjectInput, element: JQuery, view: View): void {
    // L'interno del qtip è un componente Angular!
    // In questo modo possiamo scriverlo come componente normale,
    // ma serve un po' di colla per inserirlo in un componente
    // jQuery.

    // Creo manualmente il componente!
    const tip = this.toolTipFactory.create(this.injector);

    // Passo il riferimento all'evento
    tip.instance.event = event as EventObjectInput & IAppuntamento;

    // Questo mi abilita gli eventi "OnChange"...
    this.app.attachView(tip.hostView);

    // Genero un evento di distruzione che richiamerò
    // quando il componente jQuery verrà distrutto.
    element.on('destroy', () => {
      tip.destroy();
    });

    // Carico qtip
    const qtip = element.qtip(<QTip2.QTipOptions>$.extend(true,
      {},
      Config.defaultQtip2Options,
      {
        content: {
          // Passandogli il componente angular
          // come contenuto!
          text: $(tip.location.nativeElement),
          title: event.title
        },
        position: {
          viewport: this.calendar
        }
      }
    ));

    // Aggiungo la classe a seconda dell'evento
    element.addClass(StyleComponent.sanitize(tip.instance.event.evento));

    if (event.indirizzo != null) {
      element.append(
        `
<span class="subtitle">
	${event.indirizzo} ${event.numciv}, ${event.citta})
</span>
`
      );
    }
  }

  protected onEventResize(_, event: EventObjectInput): void {
    this.updateQueue.next($.extend(event as IAppuntamento, {local: true}));
  }

  protected onEventDrop(_, event: EventObjectInput): void {
    this.updateQueue.next($.extend(event as IAppuntamento, {local: true}));
  }

  protected onViewDestroy(view: View, element: JQuery): void {
  } // Non necessaria

  protected onViewRender(view: View, element: JQuery): [Observable<IAppuntamento>, Observable<IDeleteRequest<number>>] {
    this.internalSubscription.unsubscribe();
    this.internalSubscription = new Subscription();

    // Mi connetto al WebSocket
    const currentSocket = this.agenda.getEventSocket(view.start, view.end);

    // Questo è l'unico punto in cui posso agganciarmi al socket per gli
    // aggiornamenti!

    this.internalSubscription.add(currentSocket.status.subscribe(state => {
      if (state === WebSocketReadyState.CLOSED) {
        console.log('error 4');
        document.location.reload();
        // In caso di chiusura del socket, riprovo dopo 1 sec
        this.internalSubscription.add(
          Observable.of(view)
            .pipe(delay(1000))
            .subscribe(req => this.onViewRender(view, element))
        )
      }
    }));

    this.internalSubscription.add(currentSocket.conflicts.subscribe(
      c => {
        debugger
        const modalRef = this.modalService.open(
          ConflictComponent,
          {
            size: 'lg',
            backdrop: 'static',
            keyboard: false
          }
        );

        modalRef.componentInstance.conflict = c;
        modalRef.result.then(
          (event: IAppuntamento) => this.upsertEvent(event),
          () => {
          }
        );
      }
    ));

    // Ora dobbiamo beccare gli aggiornamenti
    this.internalSubscription.add(this.updateQueue.asObservable()
      .subscribe(async evento => {
        if (evento.repeat) {
          const group_id = await this.agenda.createGroup();
          const version = +evento.version + 1;
          var start = evento.start.clone();
          var end = evento.end ? evento.end.clone() : null;
          const increment = moment.duration(
            evento.repeat.increment,
            <any>evento.repeat.incrementUnit
          );
          if (evento.repeat.type == 'occorrenze') {
            for (var i = 0; i < evento.repeat.incrementNumber; i++) {
              currentSocket.upsert({
                group_id,
                id: evento.id,
                title: evento.title,
                start: start.clone(),
                end: end ? end.clone() : null,
                indirizzo: evento.indirizzo,
                numciv: evento.numciv,
                citta: evento.citta,
                CAP: evento.CAP,
                provincia: evento.provincia,
                descrizione: evento.descrizione,
                categoria: evento.categoria,
                evento: evento.evento,
                version: version,
                status: evento.status
              });
              start = start.add(increment);
              end = end ? end.add(increment) : end;
            }
          } else {
            while ((end || start).isBefore(evento.repeat.incrementUntil)) {
              currentSocket.upsert({
                group_id,
                id: evento.id,
                title: evento.title,
                start: start.clone().add(increment),
                end: end ? end.clone().add(increment) : null,
                indirizzo: evento.indirizzo,
                numciv: evento.numciv,
                citta: evento.citta,
                CAP: evento.CAP,
                provincia: evento.provincia,
                descrizione: evento.descrizione,
                categoria: evento.categoria,
                evento: evento.evento,
                version: version,
                status: evento.status
              });
              start = start.add(increment);
              end = end ? end.add(increment) : end;
            }
          }
        } else {
          currentSocket.upsert({
            id: evento.id,
            title: evento.title,
            start: evento.start,
            end: evento.end,
            indirizzo: evento.indirizzo,
            numciv: evento.numciv,
            citta: evento.citta,
            CAP: evento.CAP,
            provincia: evento.provincia,
            descrizione: evento.descrizione,
            categoria: evento.categoria,
            evento: evento.evento,
            version: +evento.version + 1,
            status: evento.status
          })
        }
      })
    );

    this.internalSubscription.add(this.deleteQueue.asObservable()
      .subscribe(id => currentSocket.delete(id))
    );

    // Qui devo solo restituire ciò che mi passa il socket
    return [currentSocket.updates, currentSocket.deletes];
  }

  protected getBusinessHours(view: View): Observable<IIntervallo[][]> | false {
    // Nella visuale "mese" non serve l'orario completo, ma solo i giorni,
    // che vengono gestiti dal componente separato CalendarStyle, per
    // superare delle limitazioni di FullCalendar.
    if (view.type != 'month') {
      const weekStart = view.start.startOf('isoWeek');
      return this.agenda.alternativa.pipe(
        switchMap(week =>
          Observable.combineLatest(...(
              week.find(s =>
                weekStart.isSame(moment.utc(s.settimana))
              ) || this.agenda.ambulatorio
            ).map(
            amb => amb.pipe(
              switchMap(pp => pp.length ?
                Observable.combineLatest(...pp) :
                Observable.of([] as IIntervallo[])
              )
            ))
          ))
      );
    } else {
      return false;
    }
  }

  protected onCalendarDestroy() {
    this.internalSubscription.unsubscribe();
    this.internalSubscription = new Subscription();
  }


  protected init() {
    // Piccolo override: inizializziamo solo se c'è
    // un'agenda corrispondente.
    if (this.agenda) {
      super.init();
    }
  }

  upsertEvent(event?: IAppuntamento) {
    if (!event) {
      event = {
        version: 1,
        categoria: null,
        evento: null,
        title: '',
        start: moment.utc(),
        status: 'U'
      };
    }

    if (!event.id) {
      // FIXME: gli id dovrebbero essere numerici dovunque...
      event.id = `${FullCalendarComponent.creationRequestId--}`;
    }

    const modalRef = this.modalService.open(NewEventComponent);
    modalRef.componentInstance.setEvento(event);
    modalRef.result.then((event: IAppuntamento | number) => {
      if (isNumber(event)) {
        this.deleteQueue.next(event);
      } else {
        this.updateQueue.next(event);
      }
    }, () => {
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.newEventSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/calendar/full-calendar/fullcalendar.component.ts